import { z } from 'zod';
import { idempotency } from './spine';
import { listInput } from './list';

export const JOB_STAGE_OUTCOMES = ['open', 'won', 'lost'] as const;
export const JOB_STAGE_COLORS = [
  'slate',
  'blue',
  'amber',
  'green',
  'red',
  'purple',
] as const;
export type JobStageOutcome = (typeof JOB_STAGE_OUTCOMES)[number];
export type JobStageColor = (typeof JOB_STAGE_COLORS)[number];
const uuid = z.string().uuid();
const label = z.string().trim().min(1).max(80);
const expectedVersion = z.number().int().positive();

export const jobPipelineIdInput = z.object({ id: uuid }).strict();
export type JobPipelineIdInput = z.infer<typeof jobPipelineIdInput>;
export const jobPipelineCreateInput = idempotency.extend({ label }).strict();
export type JobPipelineCreateInput = z.infer<typeof jobPipelineCreateInput>;
export const jobPipelineUpdateInput = idempotency
  .extend({
    id: uuid,
    expectedVersion,
    label: label.optional(),
    active: z.boolean().optional(),
    makeDefault: z.boolean().optional(),
  })
  .strict();
export type JobPipelineUpdateInput = z.infer<typeof jobPipelineUpdateInput>;
export const jobPipelineCreateStageInput = idempotency
  .extend({
    pipelineId: uuid,
    label,
    color: z.enum(JOB_STAGE_COLORS).optional(),
    outcome: z.enum(JOB_STAGE_OUTCOMES).optional(),
  })
  .strict();
export type JobPipelineCreateStageInput = z.infer<typeof jobPipelineCreateStageInput>;
export const jobPipelineUpdateStageInput = idempotency
  .extend({
    id: uuid,
    expectedVersion,
    label: label.optional(),
    color: z.enum(JOB_STAGE_COLORS).optional(),
    outcome: z.enum(JOB_STAGE_OUTCOMES).optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type JobPipelineUpdateStageInput = z.infer<typeof jobPipelineUpdateStageInput>;
export const jobPipelineReorderStagesInput = idempotency
  .extend({
    pipelineId: uuid,
    ids: z.array(uuid).min(1).max(100),
    expectedVersion,
  })
  .strict()
  .refine((v) => new Set(v.ids).size === v.ids.length, {
    message: 'Each stage must appear once',
    path: ['ids'],
  });
export type JobPipelineReorderStagesInput = z.infer<typeof jobPipelineReorderStagesInput>;
export const jobPipelineRetireStageInput = idempotency
  .extend({ id: uuid, replacementStageId: uuid, expectedVersion })
  .strict()
  .refine((v) => v.id !== v.replacementStageId, {
    message: 'Choose a different replacement stage',
    path: ['replacementStageId'],
  });
export type JobPipelineRetireStageInput = z.infer<typeof jobPipelineRetireStageInput>;
export const jobPipelineBoardInput = z
  .object({
    pipelineId: uuid,
    stageId: uuid.optional(),
    limit: z.number().int().min(1).max(100).default(25),
    offset: z.number().int().min(0).default(0),
    search: z.string().trim().max(120).optional(),
    filters: listInput.shape.filters,
    ownerUserId: uuid.optional(),
    serviceTypeId: uuid.optional(),
    outcome: z.enum(JOB_STAGE_OUTCOMES).optional(),
  })
  .strict();
export type JobPipelineBoardInput = z.infer<typeof jobPipelineBoardInput>;
